import { EntityLinks } from '@griply/types';
import { getTimelineDateRange } from '@griply/utils';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { Goal as AppGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { GoalGroupBy, GroupByMode } from 'shared/types/sorting-mode';
import {
  TimelineDropSlot,
  TimelineScrollRequest,
  TimelineTimeframes,
} from 'shared/types/timeline';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { createOtherLifeArea } from 'shared/utils/create-other-life-area';

import { buildGoalTimelineCategories } from './build-goal-timeline-categories';
import { GoalTimeline } from './goal-timeline';
import { TimelineDragSource, TimelineReorderDrop } from './types';

export type GoalTimeLineContainerProps = {
  timeframe: TimelineTimeframes;
  referenceDate: Timestamp;
  scrollRequest: TimelineScrollRequest;
  goals: AppGoal[];
  // Unfiltered set: a sub-goal keeps its inherited life area when a filter drops its parent.
  allGoals: AppGoal[];
  lifeAreas: LifeArea[];
  links?: EntityLinks;
  weekStartsOn: WeekDays;
  title: string;
  groupBy?: GoalGroupBy;
  customOrder?: ID[];
  goalChildrenByParentId?: Map<ID, ID[]>;
  onChangeGoal?: (
    id: ID,
    updatedGoal: {
      startDate?: Timestamp;
      deadline?: Timestamp;
    },
  ) => void;
  // resolution + persistence live behind features' useTimelineDropReorder
  onReorderDrop?: (drop: TimelineReorderDrop) => void;
  onChangeCollapsedItems?: (ids: ID[]) => void;
  collapsedItems?: ID[];
  onCategoryAdd?: (id: ID, anchor: HTMLElement) => void;
};

export const GoalTimelineContainer: React.FC<GoalTimeLineContainerProps> = ({
  timeframe,
  referenceDate,
  scrollRequest,
  goals,
  allGoals,
  lifeAreas,
  links,
  weekStartsOn,
  title,
  groupBy,
  customOrder,
  goalChildrenByParentId,
  onChangeGoal,
  onReorderDrop,
  onChangeCollapsedItems,
  collapsedItems = [],
  onCategoryAdd,
}) => {
  const { t } = useTranslation();
  const taskCounts = useTaskCounts();
  const otherLifeArea = useMemo(() => createOtherLifeArea(t), [t]);

  const categories = useMemo(
    () =>
      buildGoalTimelineCategories({
        goals,
        allGoals,
        lifeAreas,
        links,
        groupBy,
        customOrder,
        goalChildrenByParentId,
        otherLifeArea,
        taskCounts: taskCounts?.byGoalId,
        goalProgressCounts: taskCounts?.byGoalProgress,
        noDeadlineName: t(
          'context-menus.filtering.task.filters.date.presets.no-deadline',
        ),
      }),
    [
      goals,
      allGoals,
      lifeAreas,
      links,
      groupBy,
      customOrder,
      goalChildrenByParentId,
      otherLifeArea,
      taskCounts?.byGoalId,
      taskCounts?.byGoalProgress,
      t,
    ],
  );

  const timelineRef = useRef<HTMLDivElement>(null);

  const dateRange = useMemo(
    () => getTimelineDateRange({ referenceDate, timeframe, weekStartsOn }),
    [referenceDate, timeframe, weekStartsOn],
  );

  const _toggleCollapse = useCallback(
    (id: ID) =>
      onChangeCollapsedItems?.(
        collapsedItems?.includes(id)
          ? collapsedItems?.filter((itemId) => itemId !== id)
          : [...collapsedItems, id],
      ),
    [collapsedItems, onChangeCollapsedItems],
  );

  const handleUpdateDates = useCallback(
    (goalId: ID, newStartDate: Timestamp, newEndDate: Timestamp) => {
      if (onChangeGoal) {
        onChangeGoal(goalId, {
          startDate: newStartDate,
          deadline: newEndDate,
        });
      }
    },
    [onChangeGoal],
  );

  const handleReorderDrop = useCallback(
    (slot: TimelineDropSlot, dragged: TimelineDragSource) => {
      onReorderDrop?.({
        ...dragged,
        slot,
        draggedGoalId: dragged.goalId,
        categories,
        goals,
        links,
        groupBy: groupBy ?? GroupByMode.None,
      });
    },
    [onReorderDrop, categories, goals, links, groupBy],
  );

  return (
    <GoalTimeline
      ref={timelineRef}
      categories={categories}
      goals={goals}
      links={links}
      lifeAreas={lifeAreas}
      dateRange={dateRange}
      onUpdateDates={handleUpdateDates}
      onReorderGoals={onReorderDrop ? handleReorderDrop : undefined}
      collapsedItems={collapsedItems}
      toggleCollapse={_toggleCollapse}
      onCategoryAdd={onCategoryAdd}
      timeframe={timeframe}
      referenceDate={referenceDate}
      scrollRequest={scrollRequest}
      weekStartsOn={weekStartsOn}
      title={title}
    />
  );
};
